// KOA-5857 — Pure helpers for the Location cell.
//
// Pure (no React, no DOM) so the paste-parser can be unit-tested
// without booting the component graph. The component imports these
// and wires them onto the inputs.

export interface IParsedLocation {
  latitude: number | null;
  longitude: number | null;
}

// Latitude is bounded ±90, longitude ±180. The cell shows out-of-range
// values with an aria-invalid border so the user notices without
// breaking the editor.
export const isLatInRange = (lat: number): boolean => {
  return lat >= -90 && lat <= 90;
};

export const isLngInRange = (lng: number): boolean => {
  return lng >= -180 && lng <= 180;
};

const stripGoogleMapsZoom = (s: string): string => {
  // Google Maps URLs look like `@40.7128,-74.0060,15z`. Drop the
  // trailing zoom token so the pair-parser sees just lat, lng.
  return s.replace(/,\s*-?\d+(?:\.\d+)?z\b/i, "");
};

const extractFromGoogleMapsUrl = (s: string): string => {
  // Pull the `@lat,lng` segment out of a maps URL. If we can't find
  // one, fall through and let the generic pair parser handle the
  // raw string.
  const match: RegExpMatchArray | null = s.match(/@(-?\d+(?:\.\d+)?,\s*-?\d+(?:\.\d+)?)/);
  return match !== null ? match[1]! : s;
};

// Parse a free-form coordinate paste into a lat/lng pair. Returns
// null when the input doesn't look like a pair — the caller should
// then let the default paste happen so the user can still paste a
// single number into one input.
//
// Recognises:
//   "40.7128, -74.0060"
//   "40.7128,-74.0060"
//   "40.7128 -74.0060"
//   "40.7128, -74.0060, 15z"       (Google Maps fragment)
//   "https://maps.google.com/@40.7128,-74.0060,15z"
//
// Does NOT recognise "40.7128° N, 74.0060° W" — DMS / hemisphere
// notation is out of scope for an inline cell paste.
export const parseLocationPaste = (raw: string): IParsedLocation | null => {
  if (raw.length === 0) {
    return null;
  }
  const cleaned: string = stripGoogleMapsZoom(extractFromGoogleMapsUrl(raw.trim()));
  // Two signed decimals separated by comma OR whitespace. Anchored so
  // a stray prefix/suffix character doesn't sneak past — the caller
  // wants either "this is a coord pair" or "not a coord pair", not a
  // best-effort partial.
  const match: RegExpMatchArray | null = cleaned.match(/^(-?\d+(?:\.\d+)?)[\s,]+(-?\d+(?:\.\d+)?)$/);
  if (match === null) {
    return null;
  }
  const lat: number = Number(match[1]);
  const lng: number = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return { latitude: lat, longitude: lng };
};

// Wire shape of the JSON the runtime LocationPicker / table editor
// writes to a Location column. Both fields are optional / nullable so
// half-populated rows survive parsing.
interface ILocationJsonShape {
  latitude?: unknown;
  longitude?: unknown;
}

// Parse a stored Location value (JSON string or already-parsed object)
// into a typed lat/lng pair. Returns `{null, null}` on malformed input
// rather than throwing — callers display an empty cell, not an error.
// Lives in helpers (not in the editor) so the read-only static
// renderer can reuse it without pulling the whole React component.
export const parseLocationValue = (raw: unknown): IParsedLocation => {
  if (raw === null || raw === undefined || raw === "") {
    return { latitude: null, longitude: null };
  }
  let candidate: unknown = raw;
  if (typeof raw === "string") {
    try {
      candidate = JSON.parse(raw);
    } catch {
      return { latitude: null, longitude: null };
    }
  }
  if (candidate === null || typeof candidate !== "object") {
    return { latitude: null, longitude: null };
  }
  const obj: ILocationJsonShape = candidate as ILocationJsonShape;
  return {
    latitude: typeof obj.latitude === "number" ? obj.latitude : null,
    longitude: typeof obj.longitude === "number" ? obj.longitude : null
  };
};

// Build a URL that opens Google Maps in browse-mode at the given
// coordinates. Uses the official `?api=1` search URL so the link is
// stable; works on web and triggers the native maps app on iOS /
// Android.
export const googleMapsUrl = (lat: number, lng: number): string => {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
};
